"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Star, Quote, ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Review {
  id: number;
  score: string;
  jobTitle: string;
  postcode: string;
  name: string;
  text: string;
}
const testimonials: Review[] = [{
  id: 2,
  score: "10/10",
  jobTitle: "New roof",
  postcode: "RG7",
  name: "Verified Checkatrade customer",
  text: "We got full new roof back in June with Martin very happy with the work that was completed and would really recommend."
}, {
  id: 4,
  score: "10/10",
  jobTitle: "New roof",
  postcode: "RG21",
  name: "Checkatrade customer",
  text: "Thanks for my new Roof guys. I'm over the moon a lovely set of lads and got it done in QuickTime for a very good price."
}, {
  id: 5,
  score: "10/10",
  jobTitle: "New felt and battens and dry ridge system",
  postcode: "RG21",
  name: "Charlotte H",
  text: "Martin and the team did a fantastic job replaced the felts in front of the roof. They replaced the old felts and installed new felt and battens and also new drive ridge system and cap ins. On top of roof We are very happy with the job they done and would certainly recommend them."
}, {
  id: 6,
  score: "10/10",
  jobTitle: "Roofing work",
  postcode: "TW12",
  name: "Verified Checkatrade customer",
  text: "Martin fixed leaks on my roof, there was 5 slipped slates which he replaced and now my roof is leak-free. Brilliant service, will use again."
}, {
  id: 7,
  score: "10/10",
  jobTitle: "Excellent Service - Fast, Professional and Reliable",
  postcode: "TW12",
  name: "Checkatrade customer",
  text: "Great work this evening from Martin and the team. We had a leak coming from the roof and called them out, and they arrived really quickly. They were professional, worked efficiently, and got the problem sorted without any hassle. Really impressed with the service and would definitely recommend them. Thanks again, Martin and the team!"
}, {
  id: 9,
  score: "10/10",
  jobTitle: "New roof",
  postcode: "SO18",
  name: "Verified Checkatrade customer",
  text: "Martin came and gave us a quote to a get a new roof. we got a couple quotes and we went with firststop roofing as my friend had them aslo for her roof i would definitely recommend them."
}];
export default function StimmenUnsererKunden() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    api.on("reInit", () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    });
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);
  const handleDotClick = (index: number) => {
    api?.scrollTo(index);
  };
  return <section data-section-id="222594" id="what-our-clients-say" className="w-full py-16 md:py-[110px] bg-secondary text-foreground overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
        {/* Header with scroll fade-up only */}
        <div ref={headerRef} className={`text-center max-w-2xl mx-auto mb-12 md:mb-16 transition-all duration-1000 ease-out motion-reduce:transition-none motion-reduce:transform-none ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs md:text-sm font-bold uppercase tracking-widest text-primary mb-3">
            TESTIMONIALS
          </p>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground mb-4">
            What Southampton Homeowners Say
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed mb-6">
            Discover why homeowners across Southampton, Hampshire &amp; surrounding areas trust roofer Matt and Firststop Roofing Ltd for all their roofing and roofline needs.
          </p>

          {/* Checkatrade Summary & Link */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 pt-2">
            <span className="font-default text-sm md:text-base font-semibold text-secondary-foreground">
              Rated 9.52/10 on Checkatrade from 9 reviews
            </span>
            <Link href="https://www.checkatrade.com/trades/firststoproofing5947343" newTab className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-[8px] bg-primary text-primary-foreground font-default text-sm font-semibold hover:bg-primary-hover shadow-sm transition-colors duration-200">
              <span>Read all our reviews on Checkatrade</span>
              <ExternalLink className="size-4 shrink-0" aria-hidden="true" />
            </Link>
          </div>
        </div>

        {/* Carousel Track without outer container fade-up (never hidden) */}
        <div className="w-full max-w-[1280px] mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-6 items-stretch">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-6 basis-full md:basis-1/2 lg:basis-1/3 flex">
                  <div className="w-full bg-card rounded-[14px] border border-border p-8 flex flex-col justify-between shadow-[0_4px_20px_-2px_rgba(21,25,29,0.06)] relative transition-all duration-300">
                    <div>
                      {/* Top row: Score badge (gold star + score out of 10) and decorative faint quote icon */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-secondary-foreground font-default text-sm font-bold" aria-label={`Rated ${testimonial.score.replace("/10", " out of 10")}`}>
                          <Star className="size-4 fill-primary text-primary shrink-0" aria-hidden="true" />
                          <span>{testimonial.score}</span>
                        </div>
                        <Quote aria-hidden="true" className="size-8 text-primary/15" />
                      </div>

                      {/* Quote text wrapped cleanly in quotes */}
                      <blockquote className="mb-6">
                        <p className="font-default text-base md:text-lg leading-relaxed text-secondary-foreground break-words">
                          "{testimonial.text}"
                        </p>
                      </blockquote>
                    </div>

                    {/* Bottom row: author info (name, job title) + postcode area pill */}
                    <div className="pt-6 border-t border-border/60 flex items-end justify-between gap-4 mt-auto">
                      <div className="min-w-0 pr-2">
                        <h3 className="font-default font-bold text-base md:text-lg text-secondary-foreground leading-snug break-words">
                          {testimonial.name}
                        </h3>
                        <p className="text-sm font-default text-muted-foreground mt-0.5 break-words">
                          {testimonial.jobTitle}
                        </p>
                      </div>

                      <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary shrink-0 font-default">
                        {testimonial.postcode}
                      </span>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {/* Carousel Controls: gold round arrow buttons & pagination dots */}
          <div className="flex items-center justify-center gap-4 mt-10 md:mt-12">
            <Button type="button" variant="default" size="icon" onClick={() => api?.scrollPrev()} aria-label="Previous testimonial" className="size-11 rounded-full bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm transition-colors duration-200">
              <ArrowLeft className="size-5" />
            </Button>

            {/* Pagination dots */}
            {count > 0 && <div className="flex items-center gap-2 px-2" role="tablist" aria-label="Testimonial slides">
                {Array.from({
              length: count
            }).map((_, index) => <button key={index} type="button" role="tab" aria-selected={current === index} aria-label={`Go to slide ${index + 1}`} onClick={() => handleDotClick(index)} className={`h-2.5 rounded-full transition-all duration-300 ${current === index ? "w-7 bg-primary" : "w-2.5 bg-border hover:bg-muted-foreground/40"}`} />)}
              </div>}

            <Button type="button" variant="default" size="icon" onClick={() => api?.scrollNext()} aria-label="Next testimonial" className="size-11 rounded-full bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm transition-colors duration-200">
              <ArrowRight className="size-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}